import { useCallback, useState } from "react";

import {
  ProvidersService,
  type ProviderCredentialResponse,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatProviderLabel } from "@/lib/provider-helpers";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface Props {
  credential: ProviderCredentialResponse | null;
  onClose: () => void;
  onRevoked: () => void;
}

/** Type-the-name confirm dialog for revoke. Same UX pattern as the
 *  FE-2 API-key revoke flow — the destructive button stays disabled
 *  until the typed string matches the credential name exactly. */
export function ProviderRevokeDialog({
  credential,
  onClose,
  onRevoked,
}: Props) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");

  const mutator = useCallback(
    (id: string) => ProvidersService.revoke(id),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<string, void>(
    mutator,
    { onSuccess: () => onRevoked() }
  );

  if (!credential) return null;

  const matchesName = typed === credential.credential_name;

  const handleClose = () => {
    reset();
    setTyped("");
    onClose();
  };

  const handleConfirm = async () => {
    if (!matchesName || isLoading) return;
    await mutate(credential.id);
    setTyped("");
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("providers.revokeDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("providers.revokeDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <Alert variant="warning">
          <div className="text-xs">
            {t("providers.revokeDialog.warning")}
          </div>
        </Alert>

        <div className="rounded-md border border-ocean-100 bg-ocean-50/40 p-3 text-sm">
          <div className="label-eyebrow mb-1">
            {t("providers.revokeDialog.targetLabel")}
          </div>
          <div className="font-medium text-ocean-900">
            {formatProviderLabel(credential.provider_type)} ·{" "}
            {credential.credential_name}
          </div>
          <div className="mt-1 text-xs text-neutral-600">
            {t("providers.revokeDialog.last4Label")}{" "}
            <code className="text-ocean-700">{credential.api_key_last_4}</code>
          </div>
        </div>

        <FormField
          label={t("providers.revokeDialog.confirmLabel", {
            name: credential.credential_name,
          })}
          value={typed}
          onChange={setTyped}
          autoFocus
          autoComplete="off"
          hint={t("providers.revokeDialog.confirmHint")}
        />

        {error && (
          <Alert variant="danger" className="mt-3">
            {parseApiError(error)}
          </Alert>
        )}
      </DialogBody>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
        >
          {t("providers.revokeDialog.cancel")}
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={handleConfirm}
          disabled={!matchesName || isLoading}
        >
          {isLoading
            ? t("providers.revokeDialog.submitLoading")
            : t("providers.revokeDialog.submit")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
